import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SecurityScanner = () => {
  const [scanning, setScanning] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [scanMode, setScanMode] = useState('camera'); // 'camera' or 'usb'
  const [usbInput, setUsbInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const usbInputRef = useRef(null);
  const lastKeypressTime = useRef(0);
  const inputBuffer = useRef('');

  // Focus USB input when in USB mode
  useEffect(() => {
    if (scanMode === 'usb' && usbInputRef.current && !validationResult) {
      usbInputRef.current.focus();
    }
  }, [scanMode, validationResult]);

  // Auto-focus USB input after showing result
  useEffect(() => {
    if (scanMode === 'usb' && !validationResult && usbInputRef.current) {
      const timer = setTimeout(() => {
        usbInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [validationResult, scanMode]);

  // Global keypress handler for USB scanner (works even without focus)
  useEffect(() => {
    if (scanMode !== 'usb' || validationResult || isProcessing) return;

    const handleGlobalKeypress = (e) => {
      const now = Date.now();
      
      // USB scanners type very fast (< 50ms between keystrokes)
      // If there's a long gap, reset the buffer
      if (now - lastKeypressTime.current > 100) {
        inputBuffer.current = '';
      }
      lastKeypressTime.current = now;

      // Enter key triggers validation
      if (e.key === 'Enter') {
        e.preventDefault();
        if (inputBuffer.current.length > 10) { // QR codes are typically > 10 chars
          processQRCode(inputBuffer.current.trim());
        }
        inputBuffer.current = '';
        setUsbInput('');
        return;
      }

      // Accumulate printable characters
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        inputBuffer.current += e.key;
        setUsbInput(inputBuffer.current);
      }
    };

    window.addEventListener('keypress', handleGlobalKeypress);
    return () => window.removeEventListener('keypress', handleGlobalKeypress);
  }, [scanMode, validationResult, isProcessing]);

  // Camera scanner setup
  useEffect(() => {
    if (scanning && scanMode === 'camera') {
      const config = {
        fps: 10,
        qrbox: function(viewfinderWidth, viewfinderHeight) {
          const minEdgePercentage = 0.7;
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return { width: qrboxSize, height: qrboxSize };
        },
        aspectRatio: 1.0,
        videoConstraints: {
          facingMode: { ideal: "environment" }
        },
        rememberLastUsedCamera: true,
        supportedScanTypes: [],
        showTorchButtonIfSupported: true
      };

      const html5QrcodeScanner = new Html5QrcodeScanner("qr-reader", config, false);
      html5QrcodeScanner.render(onScanSuccess, onScanError);
      setScanner(html5QrcodeScanner);

      return () => {
        html5QrcodeScanner.clear().catch(() => {});
      };
    }
  }, [scanning, scanMode]);

  const processQRCode = async (qrCode) => {
    if (!qrCode || isProcessing) return;
    
    setIsProcessing(true);
    
    if (scanner) {
      scanner.clear().catch(() => {});
    }
    setScanning(false);

    try {
      const response = await axios.post(`${API}/visitors/validate`, {
        qr_code: qrCode.trim(),
        scanned_by: "Oficial de Seguridad"
      });

      setValidationResult(response.data);

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setValidationResult({
        success: false,
        message: "Error al validar el código QR"
      });
      toast.error("Error al validar el código QR");
    } finally {
      setIsProcessing(false);
    }
  };

  const onScanSuccess = async (decodedText) => {
    await processQRCode(decodedText);
  };

  const onScanError = (error) => {
    if (error && !error.includes("NotFoundException")) {
      if (error.includes("NotAllowedError") || error.includes("Permission")) {
        setCameraError("Permiso de cámara denegado. Por favor, permite el acceso a la cámara.");
        toast.error("Permiso de cámara denegado");
      } else if (error.includes("NotFoundError") || error.includes("No camera")) {
        setCameraError("No se encontró ninguna cámara en este dispositivo.");
        toast.error("Cámara no encontrada");
      }
    }
  };

  const handleScanAgain = () => {
    setValidationResult(null);
    setCameraError(null);
    setScanning(true);
    setUsbInput('');
    inputBuffer.current = '';
    
    // Re-focus USB input after clearing result
    if (scanMode === 'usb') {
      setTimeout(() => usbInputRef.current?.focus(), 100);
    }
  };

  const handleUSBInputChange = (e) => {
    setUsbInput(e.target.value);
    inputBuffer.current = e.target.value;
  };

  const handleUSBKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (usbInput.trim().length > 0) {
        processQRCode(usbInput.trim());
        setUsbInput('');
        inputBuffer.current = '';
      }
    }
  };

  const switchMode = (mode) => {
    if (scanner) {
      scanner.clear().catch(() => {});
    }
    setScanner(null);
    setScanMode(mode);
    setValidationResult(null);
    setCameraError(null);
    setScanning(true);
    setUsbInput('');
    inputBuffer.current = '';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="scanner-fullscreen">
      <div className="scanner-header">
        <h1 className="scanner-title">Escanear QR</h1>
        <Link to="/" className="btn-back-scanner" data-testid="back-btn">
          ✕
        </Link>
      </div>

      {/* Mode Selector */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        padding: '10px 20px',
        background: '#1f2937',
        marginBottom: '10px'
      }}>
        <button
          onClick={() => switchMode('camera')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            background: scanMode === 'camera' ? '#10b981' : '#374151',
            color: 'white',
            transition: 'all 0.2s'
          }}
          data-testid="mode-camera-btn"
        >
          📷 Cámara
        </button>
        <button
          onClick={() => switchMode('usb')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            background: scanMode === 'usb' ? '#10b981' : '#374151',
            color: 'white',
            transition: 'all 0.2s'
          }}
          data-testid="mode-usb-btn"
        >
          🔌 Lector USB
        </button>
      </div>

      {/* Camera Scanner Mode */}
      {scanMode === 'camera' && !validationResult && !cameraError && (
        <div className="scanner-wrapper">
          <div id="qr-reader" data-testid="qr-reader"></div>
          <p className="scanner-instructions">Enfoca el código QR en el recuadro</p>
        </div>
      )}

      {/* USB Scanner Mode */}
      {scanMode === 'usb' && !validationResult && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          minHeight: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '80px',
            marginBottom: '20px',
            animation: 'pulse 2s infinite'
          }}>
            🔌
          </div>
          <h2 style={{
            color: 'white',
            fontSize: '24px',
            marginBottom: '15px'
          }}>
            Lector USB Activo
          </h2>
          <p style={{
            color: '#9ca3af',
            fontSize: '16px',
            maxWidth: '400px',
            lineHeight: '1.5',
            marginBottom: '30px'
          }}>
            Escanea el código QR con el lector USB.<br/>
            El sistema detectará automáticamente el código.
          </p>
          
          {/* Visual indicator for received input */}
          <div style={{
            background: '#374151',
            borderRadius: '12px',
            padding: '20px 30px',
            minWidth: '300px',
            border: usbInput ? '2px solid #10b981' : '2px solid #4b5563'
          }}>
            <p style={{
              color: '#9ca3af',
              fontSize: '12px',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Código detectado:
            </p>
            <p style={{
              color: usbInput ? '#10b981' : '#6b7280',
              fontSize: '18px',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              minHeight: '27px'
            }}>
              {usbInput || 'Esperando escaneo...'}
            </p>
          </div>

          {/* Hidden input for USB scanner (backup for focus) */}
          <input
            ref={usbInputRef}
            type="text"
            value={usbInput}
            onChange={handleUSBInputChange}
            onKeyDown={handleUSBKeyDown}
            style={{
              position: 'absolute',
              left: '-9999px',
              opacity: 0
            }}
            autoFocus
            data-testid="usb-input"
          />

          {/* Manual input option */}
          <div style={{ marginTop: '30px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '10px' }}>
              ¿Problemas con el lector? Ingresa el código manualmente:
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Ingresa código QR"
                value={usbInput}
                onChange={(e) => {
                  setUsbInput(e.target.value);
                  inputBuffer.current = e.target.value;
                }}
                onKeyDown={handleUSBKeyDown}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#1f2937',
                  color: 'white',
                  fontSize: '14px',
                  width: '250px'
                }}
                data-testid="manual-qr-input"
              />
              <button
                onClick={() => {
                  if (usbInput.trim()) {
                    processQRCode(usbInput.trim());
                    setUsbInput('');
                  }
                }}
                disabled={!usbInput.trim() || isProcessing}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: usbInput.trim() ? '#10b981' : '#4b5563',
                  color: 'white',
                  cursor: usbInput.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
                data-testid="validate-manual-btn"
              >
                {isProcessing ? '...' : 'Validar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Error */}
      {cameraError && scanMode === 'camera' && (
        <div className="scanner-error-fullscreen" data-testid="camera-error">
          <div className="error-icon">⚠️</div>
          <h2>Error de Cámara</h2>
          <p className="error-message">{cameraError}</p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              className="btn-retry"
              onClick={handleScanAgain}
              data-testid="retry-camera-btn"
            >
              Reintentar
            </button>
            <button
              className="btn-retry"
              onClick={() => switchMode('usb')}
              style={{ background: '#6366f1' }}
            >
              🔌 Usar Lector USB
            </button>
          </div>
        </div>
      )}

      {/* Validation Result */}
      {validationResult && (
        <div className="scanner-result-fullscreen" data-testid="validation-result">
          <div
            className={`result-card-large ${
              validationResult.success ? "result-success" : "result-error"
            }`}
          >
            <div className="result-icon-large">
              {validationResult.success ? "✅" : "❌"}
            </div>
            <h2 className="result-title" data-testid="validation-message">
              {validationResult.success ? "Acceso Permitido" : "Acceso Denegado"}
            </h2>
            <p className="result-subtitle">{validationResult.message}</p>

            {validationResult.visitor && (
              <div className="result-details-large" data-testid="visitor-details">
                <div className="detail-row">
                  <span className="detail-label">Nombre:</span>
                  <span className="detail-value">{validationResult.visitor.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tipo:</span>
                  <span className="detail-value">{validationResult.visitor.visitor_type}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Área:</span>
                  <span className="detail-value">{validationResult.visitor.area || 'N/A'}</span>
                </div>
                {validationResult.visitor.visit_reason && (
                  <div className="detail-row">
                    <span className="detail-label">Motivo:</span>
                    <span className="detail-value">{validationResult.visitor.visit_reason}</span>
                  </div>
                )}
                {validationResult.visitor.vehicle_plate && (
                  <div className="detail-row">
                    <span className="detail-label">Placa:</span>
                    <span className="detail-value">{validationResult.visitor.vehicle_plate}</span>
                  </div>
                )}
                {validationResult.visitor.vehicle_brand && (
                  <div className="detail-row">
                    <span className="detail-label">Vehículo:</span>
                    <span className="detail-value">
                      {validationResult.visitor.vehicle_brand} {validationResult.visitor.vehicle_color}
                    </span>
                  </div>
                )}
                {validationResult.visitor.number_of_visitors > 1 && (
                  <div className="detail-row">
                    <span className="detail-label">Visitantes:</span>
                    <span className="detail-value">{validationResult.visitor.number_of_visitors} personas</span>
                  </div>
                )}
                {validationResult.visitor.notes && (
                  <div className="detail-row">
                    <span className="detail-label">Notas:</span>
                    <span className="detail-value">{validationResult.visitor.notes}</span>
                  </div>
                )}
              </div>
            )}

            <button
              className="btn-scan-next"
              onClick={handleScanAgain}
              data-testid="scan-again-btn"
              autoFocus
            >
              Escanear Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Processing indicator */}
      {isProcessing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1f2937',
            padding: '30px 50px',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>⏳</div>
            <p style={{ color: 'white', fontSize: '18px' }}>Validando QR...</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default SecurityScanner;
